import { useEffect, useState } from "react";
import { api, errDetail } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle } from "lucide-react";

// Product-driven Commercial Plan inputs (2026-09 Commercial Integrity,
// Phase D) — the ONE dynamic-field renderer for every surface that lets
// staff create/edit a Payment Plan (Finance → Create & Calculate,
// Customer 360 → Finance → New/Edit Payment Plan). `GET /finance/plan-
// info/{product_code}` is the single source of truth for which fields a
// product actually needs (Tourist Visa's plain fee, Skill Migration's
// primary/spouse/dependent/PTE mix, Student Visa Premium's own subset,
// whatever a future product's Commercial Plan declares) — nothing here is
// hard-coded per product, and nothing invents a field a product's own
// Commercial Plan doesn't call for.

// usePlanInfo(productCode) — fetches plan-info whenever productCode
// changes; a product with no active/published Commercial Plan resolves to
// info=null with a clear error string, never an invented default.
export const usePlanInfo = (productCode) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setInfo(null); setError("");
    if (!productCode) return;
    setLoading(true);
    api.get(`/finance/plan-info/${productCode}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(errDetail(e)))
      .finally(() => setLoading(false));
  }, [productCode]);
  return { info, error, loading };
};

// NoCommercialPlan — the explicit "nothing invented" state for a product
// like IELTS/PTE that currently has no active Commercial Plan at all.
export const NoCommercialPlan = ({ productCode }) => (
  <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground flex items-start gap-2"
      data-testid="no-commercial-plan">
    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
    <span>No Commercial Plan configured for <b>{productCode}</b> yet — an admin needs to publish one in
      Administration → Commercial Plans before a payment plan can be created for this product.</span>
  </div>
);

// PlanInputsFields — the dynamic field list itself, plus the total-
// override/discount pair (hidden entirely for a simple_fee product, whose
// single "professional_fee" input already IS the amount staff enters —
// showing a redundant override on top of it would be confusing, and
// mirrors Create & Calculate's own existing, correct behavior exactly).
export const PlanInputsFields = ({
  info, inputs, onInputsChange,
  totalAgreed, onTotalAgreedChange,
  totalOverrideLabel = "Total override (₹, optional — raises the total only)",
  discount, onDiscountChange, showDiscount = false,
  testIdPrefix = "plan-input",
}) => {
  if (!info) return null;
  return (
    <>
      <p className="text-[11px] text-muted-foreground">
        Commercial Plan: <b>{info.plan_name}</b> (v{info.version})
        {info.third_trigger && <> · 3rd instalment trigger: <b>{info.third_trigger.replaceAll("_", " ")}</b></>}
      </p>
      {info.inputs.map((f) => f.type === "bool" ? (
        <div key={f.key} className="flex items-center justify-between">
          <Label className="text-xs">{f.label}</Label>
          <Switch data-testid={`${testIdPrefix}-${f.key}`} checked={!!inputs[f.key]}
            onCheckedChange={(v) => onInputsChange({ ...inputs, [f.key]: v })} />
        </div>
      ) : (
        <div key={f.key} className="space-y-1">
          <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
          <Input data-testid={`${testIdPrefix}-${f.key}`} type={f.type === "text" ? "text" : "number"} className="h-8"
            value={inputs[f.key] ?? ""}
            onChange={(e) => onInputsChange({
              ...inputs,
              [f.key]: f.type === "int" ? Number(e.target.value || 0) : e.target.value,
            })} />
        </div>
      ))}
      {info.mode !== "simple_fee" && (onTotalAgreedChange || (showDiscount && onDiscountChange)) && (
        <div className={showDiscount ? "grid grid-cols-2 gap-2" : ""}>
          {onTotalAgreedChange && (
            <div className="space-y-1"><Label className="text-xs">{totalOverrideLabel}</Label>
              <Input data-testid={`${testIdPrefix}-total-agreed`} type="number" className="h-8"
                placeholder="Leave blank to use the calculated total"
                value={totalAgreed} onChange={(e) => onTotalAgreedChange(e.target.value)} /></div>
          )}
          {showDiscount && onDiscountChange && (
            <div className="space-y-1"><Label className="text-xs">Discount (₹)</Label>
              <Input data-testid={`${testIdPrefix}-discount`} type="number" className="h-8"
                value={discount} onChange={(e) => onDiscountChange(e.target.value)} /></div>
          )}
        </div>
      )}
    </>
  );
};

// buildInputsPayload — the {key: value} object create/update actually
// send, filtered to keys this product's OWN Commercial Plan declared
// (info.inputs) — never a fixed field list, so a product that doesn't use
// e.g. spouse_migrating never has it silently included.
export const buildInputsPayload = (info, inputs) => {
  const payload = {};
  for (const f of info?.inputs || []) {
    if (f.key in inputs) payload[f.key] = inputs[f.key];
  }
  return payload;
};
